import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBookings, useDaily, useRooms } from '../api/queries';
import { useReorderList } from '../api/stock';
import { formatDate, formatGhs } from '../format';
import { Empty, ErrorNote, Loading } from '../ui/kit';
import { BookingItem, PageHead } from './shared';

export function Today() {
  const { me, can } = useAuth();
  const canSeeBookings = can('booking.view');
  const today = me?.property.today ?? '';

  const arrivals = useBookings({ arrivingOn: today, limit: 100 }, canSeeBookings);
  const departures = useBookings({ departingOn: today, limit: 100 }, canSeeBookings);
  const inHouse = useBookings({ status: 'CHECKED_IN', limit: 200 }, canSeeBookings);
  const rooms = useRooms();
  const daily = useDaily(today, can('reports.view'));
  const reorder = useReorderList(can('stock.view'));
  const toBuy = reorder.data?.items ?? [];

  // Staff without bookings land on their own work: the floor, the kitchen, or the rooms board.
  if (!canSeeBookings) return <Navigate to={can('order.create') ? '/restaurant' : can('kitchen.view') ? '/kitchen' : can('pool.view') ? '/pool' : can('transport.drive') ? '/trips' : '/rooms'} replace />;

  const arriving = (arrivals.data ?? []).filter((b) => b.status === 'CONFIRMED' || b.status === 'PENDING');
  const leaving = (departures.data ?? []).filter((b) => b.status === 'CHECKED_IN');
  const dirty = (rooms.data ?? []).filter((r) => r.status === 'VACANT_DIRTY').length;
  const totalRooms = rooms.data?.length ?? 0;
  const occupied = (rooms.data ?? []).filter((r) => r.status === 'OCCUPIED').length;

  return (
    <div className="page">
      <PageHead eyebrow={today ? formatDate(today, true) : ''} title={`Good day, ${me?.name.split(' ')[0] ?? ''}`}>
        {can('booking.create') ? (
          <Link to="/new" className="btn primary">
            New booking
          </Link>
        ) : null}
      </PageHead>

      <section className="stats" aria-label="Today at a glance">
        <div className="card stat">
          <span className="eyebrow">Arriving</span>
          <span className="value num">{arrivals.isLoading ? '–' : arriving.length}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">In house</span>
          <span className="value num">{inHouse.isLoading ? '–' : inHouse.data?.length ?? 0}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">Departing</span>
          <span className="value num">{departures.isLoading ? '–' : leaving.length}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">Rooms to clean</span>
          <span className="value num">{rooms.isLoading ? '–' : dirty}</span>
          <span className="hint">{totalRooms ? `${occupied} of ${totalRooms} occupied` : ''}</span>
        </div>
      </section>

      {toBuy.length > 0 ? (
        <Link to="/stock?tab=buy" className="card flat" style={{ textDecoration: 'none', color: 'inherit' }} aria-label="Items to buy">
          <span className="eyebrow">Stock to buy</span>{' '}
          <strong>
            {toBuy.length} item{toBuy.length === 1 ? '' : 's'}
          </strong>
          <span className="small muted">
            {' '}
            · {toBuy.filter((i) => i.state === 'OUT').length} out, {toBuy.filter((i) => i.state === 'LOW').length} running low. Open the buy list
          </span>
        </Link>
      ) : null}

      {daily.data ? (
        <section className="card flat" aria-label="Today's money">
          <div className="card-head">
            <h2 className="h2">Money today</h2>
            <Link to="/night-audit" className="small">
              Night audit
            </Link>
          </div>
          <div className="stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <div className="stat">
              <span className="eyebrow">Revenue</span>
              <span className="value num">{formatGhs(daily.data.totalRevenue)}</span>
            </div>
            <div className="stat">
              <span className="eyebrow">Received</span>
              <span className="value num">{formatGhs(daily.data.totalReceived)}</span>
            </div>
            <div className="stat">
              <span className="eyebrow">Occupancy</span>
              <span className="value num">{daily.data.rooms.occupancyPct}%</span>
            </div>
          </div>
        </section>
      ) : null}

      <div className="grid-2">
        <section aria-labelledby="arr">
          <div className="card-head">
            <h2 className="h2" id="arr">
              Arrivals
            </h2>
          </div>
          {arrivals.isLoading ? (
            <Loading rows={2} />
          ) : arrivals.error ? (
            <ErrorNote error={arrivals.error} retry={() => void arrivals.refetch()} />
          ) : arriving.length === 0 ? (
            <div className="card flat">
              <Empty icon="inbox" title="No more arrivals today" />
            </div>
          ) : (
            <div className="list">
              {arriving.map((b) => (
                <BookingItem key={b.id} b={b} />
              ))}
            </div>
          )}
        </section>

        <section aria-labelledby="dep">
          <div className="card-head">
            <h2 className="h2" id="dep">
              Departures
            </h2>
          </div>
          {departures.isLoading ? (
            <Loading rows={2} />
          ) : departures.error ? (
            <ErrorNote error={departures.error} retry={() => void departures.refetch()} />
          ) : leaving.length === 0 ? (
            <div className="card flat">
              <Empty icon="inbox" title="Nobody checking out today" />
            </div>
          ) : (
            <div className="list">
              {leaving.map((b) => (
                <BookingItem key={b.id} b={b} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
